import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { getCurrentGroupSummary } from "@/reducers/dashboardReducer/dashboardThunks";

import DashboardCard from "./Feature/DashboardCard";
import { SwaggerAPI } from "@/api/hunger.api";
import ReservationAnalysis from "@/components/Feature/ReservationAnalysis";
import { useAsync } from "react-use";
import {
  convertDateToZeroZone,
  getDayEnd,
  getDayStart,
} from "@/helper/dateHelper";
import classNames from "classnames";
import {
  MgtGroupOrderAnalyzeDto,
  MgtGroupVoucherAnalyzeDto,
} from "@/api/swagger";
import CustomerVisitCountAnalysis from "@/components/Feature/CustomerVisitCountAnalysis";
import { HungerTabSet } from "@/components/General/Tab";
import { numberWithComma } from "@/helper/formatter";
import Table, { Column } from "@/components/Feature/Table";
import ConsumerAgeDistributionAnalysis from "@/components/Feature/ConsumerAgeDistributionAnalysis";
import { hungerMoment } from "@/moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileChartLine } from "@fortawesome/pro-solid-svg-icons";
import Flexbox from "@/components/General/Flexbox";
import { isNotSet } from "@/tools/format.checker";
import download from "@/helper/download";

type Props = ReduxProps<typeof connector>;

const GroupDashboard: React.FC<Props> = ({
  getCurrentGroupSummary,
  wholeSummary,
}) => {
  useEffect(() => {
    getCurrentGroupSummary();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <ul className="dashboard__list">
        <DashboardCard
          title="累積會員數"
          unit="位"
          count={wholeSummary.totalMemberCount}
          icon={["far", "users"]}
        />
        <DashboardCard
          title="累積訂單數"
          unit="件"
          count={wholeSummary.totalOrderCount}
          icon={["far", "receipt"]}
        />
        <DashboardCard
          title="累積總消費金額"
          unit="元"
          count={wholeSummary.totalCheckoutPrice}
          icon={["far", "sack-dollar"]}
          sign="$"
        />
        <DashboardCard
          title="儲值金"
          unit="元"
          count={wholeSummary.totalDepositPoint}
          icon={["far", "usd-circle"]}
          sign="$"
        />
      </ul>
      <div className={""}>
        <HungerTabSet
          tabs={["預約分析", "會員分析", "票券分析", "儲值金分析", "報表下載"]}
          mode={"gather"}
        >
          <div>
            <DataAnalysis />
            <div>
              <ReservationAnalysis
                displayTitle
                searchOrderDailyCountApi={SwaggerAPI.searchGroupOrderDailyCount}
              />
            </div>
            <CustomerVisitCountAnalysis
              searchOrderDailyCountApi={
                SwaggerAPI.searchGroupOrderCustomerVisitCount
              }
            />
          </div>
          <MemberAnalysis />
          <VoucherAnalysis />
          <DepositPointAnalysis />
          <Report />
        </HungerTabSet>
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  wholeSummary: state.dashboard.group.whole,
});

const mapDispatchToProps = {
  getCurrentGroupSummary,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(GroupDashboard);

const getRatio = (
  dto: MgtGroupOrderAnalyzeDto | undefined | null,
  type: keyof Omit<MgtGroupOrderAnalyzeDto, "totalOrderCount">
) => {
  if (!dto) return "0.0%";

  const total = dto.totalOrderCount;
  const value = dto[type];
  let summary = (value / total) * 100;
  if (isNaN(summary)) summary = 0;

  return `${summary.toFixed(1)}`;
};

interface DataAnalysisData {
  type: keyof Omit<MgtGroupOrderAnalyzeDto, "totalOrderCount">;
  thirty: MgtGroupOrderAnalyzeDto;
  hundredEighty: MgtGroupOrderAnalyzeDto;
  threeHundredSixty: MgtGroupOrderAnalyzeDto;
}

const DataAnalysis = () => {
  const { value = [] } = useAsync(async () => {
    const today = getDayStart(hungerMoment());
    const TDaysAgo = getDayEnd(today.clone().subtract(30, "days"));
    const OHEDaysAgo = getDayEnd(today.clone().subtract(180, "days"));
    const THSDaysAgo = getDayEnd(today.clone().subtract(360, "days"));

    const [
      { data: thirty },
      { data: hundredEighty },
      { data: threeHundredSixty },
    ] = await Promise.all([
      await SwaggerAPI.searchGroupPeriodOrderCount({
        time: {
          from: convertDateToZeroZone(TDaysAgo),
          until: convertDateToZeroZone(today),
        },
      }),
      await SwaggerAPI.searchGroupPeriodOrderCount({
        time: {
          from: convertDateToZeroZone(OHEDaysAgo),
          until: convertDateToZeroZone(today),
        },
      }),
      await SwaggerAPI.searchGroupPeriodOrderCount({
        time: {
          from: convertDateToZeroZone(THSDaysAgo),
          until: convertDateToZeroZone(today),
        },
      }),
    ]);

    const rowKeys = [
      "checkedInCount",
      "noShowCount",
      "cancelCount",
      "regularConsumerCount",
      "specifyCount",
    ] as Array<keyof Omit<MgtGroupOrderAnalyzeDto, "totalOrderCount">>;

    return rowKeys.map<DataAnalysisData>((key) => ({
      type: key,
      thirty,
      hundredEighty,
      threeHundredSixty,
    }));
  }, []);

  const columns = useMemo<
    Array<
      Column<{
        type: keyof Omit<MgtGroupOrderAnalyzeDto, "totalOrderCount">;
        thirty: MgtGroupOrderAnalyzeDto;
        hundredEighty: MgtGroupOrderAnalyzeDto;
        threeHundredSixty: MgtGroupOrderAnalyzeDto;
      }>
    >
  >(() => {
    const dataTitles: Record<
      keyof Omit<MgtGroupOrderAnalyzeDto, "totalOrderCount">,
      string
    > = {
      checkedInCount: "出席率",
      noShowCount: "爽約率",
      cancelCount: "取消率",
      regularConsumerCount: "熟客率",
      specifyCount: "指定率",
    };

    return [
      {
        accessor: "type",
        header: "數據",
        flex: 2,
        formatter: ({ type }) => dataTitles[type],
      },
      {
        accessor: "thirty",
        header: "最近 30 天",
        formatter: ({ type, thirty }) => `${getRatio(thirty, type)}%`,
      },
      {
        accessor: "hundredEighty",
        header: "最近 180 天",
        formatter: ({ type, hundredEighty }) =>
          `${getRatio(hundredEighty, type)}%`,
      },
      {
        accessor: "threeHundredSixty",
        header: "最近 360 天",
        formatter: ({ type, threeHundredSixty }) =>
          `${getRatio(threeHundredSixty, type)}%`,
      },
    ];
  }, []);

  return (
    <div className={"mb-6 bg-white rounded-xl"}>
      <div
        className={classNames(
          "flex flex-col md:flex-row md:items-center justify-between",
          "px-5 md:px-10 py-3 md:py-5",
          "border-b border-solid border-grey-4"
        )}
      >
        <p
          className={"text-2xl mb-2 md:mb-auto text-primary-pink font-semibold"}
        >
          數據統計
        </p>
        <p className={"md:ml-auto text-sm text-grey-2"}>
          最近 6 個月內，共{" "}
          {numberWithComma(value[0]?.threeHundredSixty.totalOrderCount ?? 0)}{" "}
          筆預約
        </p>
      </div>
      <Table data={value} columns={columns} />
    </div>
  );
};

const MemberAnalysis = () => {
  const { value = [] } = useAsync(async () => {
    const { data } = await SwaggerAPI.getGroupConsumerAnalyze();

    return data;
  }, []);

  return <ConsumerAgeDistributionAnalysis data={value} />;
};

const VoucherAnalysis = () => {
  const { value = [] } = useAsync(async () => {
    const { data } = await SwaggerAPI.getGroupVoucherAnalyze();

    return data;
  }, []);

  const columns = useMemo<Array<Column<MgtGroupVoucherAnalyzeDto>>>(() => {
    return [
      {
        header: "票券",
        flex: 3,
        accessor: "voucherTemplateName",
      },
      {
        header: "發出數量",
        accessor: "sentCount",
        formatter: ({ sentCount }) => numberWithComma(sentCount),
        sortable: true,
      },
      {
        header: "過期數量",
        accessor: "expiredCount",
        formatter: ({ expiredCount }) => numberWithComma(expiredCount),
        sortable: true,
      },
      {
        header: "核銷數量",
        accessor: "usedCount",
        formatter: ({ usedCount }) => numberWithComma(usedCount),
        sortable: true,
      },
      {
        header: "剩餘數量",
        accessor: "availableCount",
        sortingValueFormatter: ({ availableCount }) =>
          numberWithComma(availableCount),
        formatter: ({ availableCount }) => (
          <span className={"text-green"}>
            {numberWithComma(availableCount)}
          </span>
        ),
        sortable: true,
      },
    ];
  }, []);

  return <Table data={value} columns={columns} />;
};

interface DepositPointAnalysisData {
  type: "used" | "remain";
  count: string;
  ratio: string;
}

const DepositPointAnalysis = () => {
  const { value = [] } = useAsync(async () => {
    const { data } = await SwaggerAPI.getGroupDepositPointAnalyze();
    const sent = data.sentDepositPoint;
    const remain = data.remainDepositPoint;
    const used = sent - remain;

    return [
      {
        type: "used",
        count: numberWithComma(used),
        ratio: `${((used / sent) * 100).toFixed(1)}%`,
      },
      {
        type: "remain",
        count: numberWithComma(remain),
        ratio: `${((remain / sent) * 100).toFixed(1)}%`,
      },
    ] as Array<DepositPointAnalysisData>;
  }, []);

  const columns = useMemo<Array<Column<DepositPointAnalysisData>>>(() => {
    const depositPointLabels: Record<DepositPointAnalysisData["type"], string> =
      {
        used: "已使用儲值金",
        remain: "剩餘儲值金",
      };

    return [
      {
        header: "儲值金",
        accessor: "type",
        flex: 5,
        formatter: ({ type }) => depositPointLabels[type],
      },
      { header: "佔比", accessor: "ratio" },
      {
        header: "金額",
        accessor: "count",
      },
    ];
  }, []);

  return <Table data={value} columns={columns} />;
};

const Report: React.FC = () => {
  const onMemberDataDownloadClick = useCallback(async () => {
    const { data } = await SwaggerAPI.downloadGroupConsumerReport();
    if (isNotSet(data.url)) {
      return;
    }
    download(data.url);
  }, []);
  const onMemberOwnVoucherDownloadClick = useCallback(async () => {
    const { data } = await SwaggerAPI.downloadGroupConsumerVoucherReport();
    if (isNotSet(data.url)) {
      return;
    }
    download(data.url);
  }, []);

  return (
    <div>
      <p
        className={
          "px-12 py-[1.125rem] text-primary-pink font-semibold border-b border-grey-4"
        }
      >
        會員資料
      </p>
      <DownloadRow title={"會員資料"} onClick={onMemberDataDownloadClick} />
      <DownloadRow
        title={"會員持有票券清單"}
        onClick={onMemberOwnVoucherDownloadClick}
      />
    </div>
  );
};

const DownloadRow: React.FC<{ title: string; onClick: () => void }> = ({
  title,
  onClick,
}) => {
  return (
    <Flexbox
      justify={"between"}
      align={"center"}
      className={"px-12 py-3 border-b border-grey-4"}
    >
      <span className={"text-grey-2"}>
        <span className={"mr-2"}>
          <FontAwesomeIcon icon={faFileChartLine} />
        </span>
        {title}
      </span>
      <button
        type="button"
        onClick={onClick}
        className={classNames(
          "py-1 px-[2.125rem]",
          "text-primary-pink hover:text-white",
          "border border-primary-pink rounded-full",
          "bg-white hover:bg-primary-pink"
        )}
      >
        下載
      </button>
    </Flexbox>
  );
};
